import { generateModelConceptIri } from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import { StardogFeatureSupported } from 'src/store/features/types';
import { VirtualGraphOption } from 'src/store/state/dataSources';
import {
  Project,
  ProjectFileUpload,
  ProjectVirtualGraph,
} from 'src/store/state/projects';
import {
  SUGGESTIONS_AUTO_CREATE_IRI,
  SUGGESTIONS_CSV_QUOTE_IRI,
  SUGGESTIONS_CSV_SEPARATOR_IRI,
  SUGGESTIONS_MODEL_IRI,
  SUGGESTIONS_SOURCE_DATA_IRI,
  SUGGESTIONS_SOURCE_IRI,
  SUGGESTIONS_SOURCE_NAME_IRI,
  SUGGESTIONS_SOURCE_QUERY_IRI,
  SUGGESTIONS_SOURCE_TABLE_IRI,
  SUGGESTIONS_SQL_QUOTING_IRI,
  SUGGESTIONS_TARGET_IRI,
  SUGGESTIONS_TARGET_NAMESPACE_IRI,
  SUGGESTIONS_TARGET_TYPE_IRI,
} from 'src/templates/queries/iris';
import { formatSeparator } from 'src/utils/csv/formatSeparator';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';

const createConfigFromRecord = (lines: Record<string, string>) =>
  `${Object.entries(lines)
    .filter(([, value]) => Boolean(value))
    .map(([key, value]) => `<${key}> ${value}`)
    .join(' ;\n    ')} .`;

export const createConfigForCustomVirtualGraph = (
  project: Project,
  virtualGraph: ProjectVirtualGraph,
  query: string,
  classIris: string[],
  stardogFeatures: StardogFeatureSupported
) =>
  createConfigFromRecord({
    [SUGGESTIONS_SOURCE_IRI]: `<data-source://${virtualGraph.dataSourceId}>`,
    [SUGGESTIONS_SOURCE_NAME_IRI]: `"${escapeDoubleQuotes(virtualGraph.name)}"`,
    [SUGGESTIONS_SOURCE_QUERY_IRI]: `"""${
      escapeDoubleQuotes(query) // must use triple quotes for new line char
    }"""`,
    [SUGGESTIONS_TARGET_IRI]: `<${
      classIris.length ? SUGGESTIONS_MODEL_IRI : SUGGESTIONS_AUTO_CREATE_IRI
    }>`,
    ...(classIris.length
      ? {
          [SUGGESTIONS_TARGET_TYPE_IRI]:
            stardogFeatures.isAutoMappingTargetTypeSupported
              ? classIris.map((classIri) => `<${classIri}>`).join(', ')
              : '',
        }
      : {
          [SUGGESTIONS_TARGET_NAMESPACE_IRI]: `<${generateModelConceptIri(
            project
          )}>`,
        }),
    [SUGGESTIONS_SQL_QUOTING_IRI]: `"${escapeDoubleQuotes(
      virtualGraph.options[VirtualGraphOption.SQL_QUOTING_CONVENTION]
    )}"`,
  });

// TODO: this is dead code and will need to be adapted for whatever the API ends
// up looking like in 8.2.1 (will probably need to add some subset of virtual
// graph options, as well as catalog / schema as parameters)
export const createConfigForTableVirtualGraphs = (
  project: Project,
  dataSourceId: string,
  tableNames: string[],
  classIris: string[],
  stardogFeatures: StardogFeatureSupported
) =>
  createConfigFromRecord({
    [SUGGESTIONS_SOURCE_IRI]: `<data-source://${dataSourceId}>`,
    [SUGGESTIONS_SOURCE_TABLE_IRI]: `"${tableNames
      .map((tableName) => escapeDoubleQuotes(tableName))
      .join('", "')}"`,
    [SUGGESTIONS_TARGET_IRI]: `<${
      classIris.length ? SUGGESTIONS_MODEL_IRI : SUGGESTIONS_AUTO_CREATE_IRI
    }>`,
    ...(classIris.length
      ? {
          [SUGGESTIONS_TARGET_TYPE_IRI]:
            stardogFeatures.isAutoMappingTargetTypeSupported
              ? classIris.map((classIri) => `<${classIri}>`).join(', ')
              : '',
        }
      : {
          [SUGGESTIONS_TARGET_NAMESPACE_IRI]: `<${generateModelConceptIri(
            project
          )}>`,
        }),
  });

export const createConfigForFileUpload = (
  project: Project,
  fileUpload: ProjectFileUpload,
  classIris: string[],
  stardogFeatures: StardogFeatureSupported
) =>
  createConfigFromRecord({
    [SUGGESTIONS_SOURCE_NAME_IRI]: `"${escapeDoubleQuotes(fileUpload.name)}"`,
    [SUGGESTIONS_CSV_QUOTE_IRI]: `"${escapeDoubleQuotes(
      fileUpload.options.quote
    )}"`,
    [SUGGESTIONS_CSV_SEPARATOR_IRI]: `"${escapeDoubleQuotes(
      formatSeparator(fileUpload.options.separator)
    )}"`,
    [SUGGESTIONS_SOURCE_DATA_IRI]: `"""${
      escapeDoubleQuotes(fileUpload.contents.trim()) // must use triple quotes for new line char
    }"""`,
    [SUGGESTIONS_TARGET_IRI]: `<${
      classIris.length ? SUGGESTIONS_MODEL_IRI : SUGGESTIONS_AUTO_CREATE_IRI
    }>`,
    ...(classIris.length
      ? {
          [SUGGESTIONS_TARGET_TYPE_IRI]:
            stardogFeatures.isAutoMappingTargetTypeSupported
              ? classIris.map((classIri) => `<${classIri}>`).join(', ')
              : '',
        }
      : {
          [SUGGESTIONS_TARGET_NAMESPACE_IRI]: `<${generateModelConceptIri(
            project
          )}>`,
        }),
  });
